import React, { ReactElement } from "react";
import styles from "./styles.module.scss";
import { DomainPostItemType } from "pages/api/fetchDomainPosts";
import { FilterOption } from "../filterSidebar";
import { replacePlaceholders, stripHtmlTags } from "@lib/sharedUtils";
import TimeIcon from "./icons/timeIcon";
import CalendarIcon from "./icons/calendarIcon";
import {
    getPublishedDateLabel,
    getPostFieldValue,
} from "@page-builder/utils/index";
import ArrowIcon from "./icons/arrowIcon";

function truncateExcerpt(html: string, maxChars = 110): string {
    const plain = stripHtmlTags(html).trim();
    if (plain.length <= maxChars) return plain;
    const cut = plain.lastIndexOf(" ", maxChars);
    return plain.slice(0, cut > 0 ? cut : maxChars);
}

interface PostCardProps {
    post: DomainPostItemType;
    type: "blog-type_post" | "blog-type_projectGuide";
    readMoreText?: string;
    filters?: FilterOption[];
    hideCardTag?: boolean;
    hideReadMoreButton?: boolean;
    hideFactorsSection?: boolean;
}

export default function PostCard({
    post,
    type,
    readMoreText = "Read More",
    hideCardTag = false,
    hideReadMoreButton = false,
    hideFactorsSection = false,
}: PostCardProps): ReactElement {
    const tagLabel = (
        getPostFieldValue(
            post,
            type === "blog-type_post"
                ? "bestContractorsPostImageTag"
                : "bestContractorsProjectGuideImageTag",
        ) as { label?: string } | undefined
    )?.label;

    const tagIcon = getPostFieldValue(
        post,
        type === "blog-type_post"
            ? "bestContractorsPostImageIcon"
            : "bestContractorsProjectGuideImageIcon",
    );

    const factors = (
        getPostFieldValue(post, "factorsAffectingCost") as
            | { label?: string }[]
            | undefined
    )?.map((item) => item?.label ?? "");

    const costRange = (
        getPostFieldValue(post, "keyCostInformation") as
            | undefined
            | { title?: string; description?: string }[]
    )?.find((item) =>
        item?.title?.toLowerCase().includes("average cost range"),
    )?.description;

    const readingTime = getPostFieldValue(post, "postReadingTime") as string;

    const href = post?.url?.url
        ? type === "blog-type_post"
            ? `${post.url.url}`
            : `/project-guide${post.url.url}`
        : undefined;

    const cardContent = (
        <>
            <div className={styles.cardImageWrapper}>
                {post?.image?.fullPath ? (
                    <img
                        src={post.image.fullPath}
                        alt={post.title || "Post image"}
                    />
                ) : (
                    <div className={styles.placeholderImage}>No image</div>
                )}

                {!hideCardTag && tagLabel && (
                    <div className={styles.cardTag}>
                        {tagIcon && (
                            <img
                                src={tagIcon as string}
                                alt={tagLabel || "Tag icon"}
                                className={styles.cardTagIcon}
                            />
                        )}
                        <span className={styles.cardTagLabel}>{tagLabel}</span>
                    </div>
                )}
            </div>

            <div className={styles.cardBody}>
                <h3 className={styles.cardTitle}>
                    {replacePlaceholders(post.title)}
                </h3>

                {type === "blog-type_post" && (
                    <>
                        <div className={styles.cardMeta}>
                            {readingTime && (
                                <>
                                    <span className={styles.cardMetaItem}>
                                        <TimeIcon />
                                        <span className={styles.readingTime}>
                                            {Number(readingTime) > 1
                                                ? `${readingTime} mins read`
                                                : `${readingTime} min read`}
                                        </span>
                                    </span>
                                    <span
                                        className={styles.cardMetaDot}
                                        aria-hidden="true"
                                    >
                                        •
                                    </span>
                                </>
                            )}
                            <span className={styles.cardMetaItem}>
                                <CalendarIcon />
                                <span className={styles.updatedAt}>
                                    {getPublishedDateLabel(post)}
                                </span>
                            </span>
                        </div>

                        {post?.excerpt && (
                            <div className={styles.cardExcerpt}>
                                {truncateExcerpt(
                                    replacePlaceholders(post.excerpt),
                                )}{" "}
                                {!hideReadMoreButton && (
                                    <span className={styles.readMore}>
                                        {readMoreText}
                                    </span>
                                )}
                            </div>
                        )}
                    </>
                )}

                {type === "blog-type_projectGuide" && (
                    <>
                        {costRange && (
                            <>
                                <div className={styles.averageCost}>
                                    Average Cost
                                </div>
                                <div className={styles.costRange}>
                                    {costRange}
                                </div>
                            </>
                        )}
                        {!hideReadMoreButton && (
                            <span className={`${styles.readMoreProjectGuide}`}>
                                <span className={styles.readMoreText}>
                                    {readMoreText}
                                </span>
                                <span>
                                    <ArrowIcon />
                                </span>
                            </span>
                        )}
                        {!hideFactorsSection && factors && factors.length > 0 && (
                            <div className={styles.factorsSection}>
                                <span className={styles.factorsTitle}>
                                    Factors
                                </span>
                                <div className={styles.factorsList}>
                                    {factors.map((factor) => (
                                        <div
                                            key={factor}
                                            className={styles.factorItem}
                                        >
                                            {factor}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );

    if (!href) {
        return <article className={styles.card}>{cardContent}</article>;
    }

    return (
        <a href={href} className={styles.card}>
            {cardContent}
        </a>
    );
}
